import React from "react";
import { motion } from "framer-motion";
import { ConversationMeta } from "@/utils/axios/claudeUtils.ts";
import { PlusCircle, Sparkles, Trash2, X } from "lucide-react";
import ConversationItem from "@/pages/API/ClaudeChat/parts/ConversationItem.tsx";

interface ClaudeChatSidebarProps {
  conversations: ConversationMeta[];
  activeConversationId?: string;
  isMobileView: boolean;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
  onClearAllConversations: () => void;
}

const ClaudeChatSidebar: React.FC<ClaudeChatSidebarProps> = ({
  conversations,
  activeConversationId,
  isMobileView,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onClearAllConversations,
}) => {
  // Animation variants
  const sidebarVariants = {
    hidden: { x: "-100%" },
    visible: { x: 0 },
  };

  return (
    <motion.div
      className="flex flex-col h-full w-full"
      initial={isMobileView ? "hidden" : "visible"}
      animate="visible"
      variants={sidebarVariants}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={{ maxWidth: "100%", maxHeight: "100vh" }}
    >
      {/* En-tête */}
      <div className="p-3 md:p-4 border-b border-base-200 flex justify-between items-center">
        <h2 className="text-lg font-semibold flex items-center">
          <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
          Claude
        </h2>
        {isMobileView && activeConversationId && (
          <button
            onClick={() => onSelectConversation(activeConversationId)}
            className="btn btn-ghost btn-sm btn-square"
            aria-label="Fermer la barre latérale"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Bouton Nouvelle conversation */}
      <div className="p-3 md:p-4">
        <button
          onClick={onNewConversation}
          className="btn btn-primary w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 border-none text-sm md:text-base py-2 h-auto"
        >
          <PlusCircle className="w-4 h-4 md:w-5 md:h-5" />
          Nouvelle conversation
        </button>
      </div>

      {/* Liste des conversations */}
      <div className="flex-1 overflow-y-auto min-h-0 p-2">
        {conversations.length === 0 ? (
          <div className="text-center p-4 text-gray-500 text-sm">
            Aucune conversation
          </div>
        ) : (
          <ul className="space-y-1 w-full">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isActive={conversation.id === activeConversationId}
                onSelect={onSelectConversation}
                onDelete={onDeleteConversation}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Pied avec bouton pour effacer toutes les conversations */}
      <div className="p-3 border-t border-base-200 mt-auto">
        <button
          onClick={onClearAllConversations}
          className="btn btn-ghost btn-sm w-full flex items-center justify-center gap-2 text-red-500 hover:bg-red-50 text-xs md:text-sm"
          disabled={conversations.length === 0}
        >
          <Trash2 className="w-4 h-4" />
          Effacer toutes les conversations
        </button>
        <div className="text-xs text-center mt-3 text-gray-500">
          © {new Date().getFullYear()} YaCadMander
        </div>
      </div>
    </motion.div>
  );
};

export default ClaudeChatSidebar;
